import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchBooths,
  fetchAdminReservations,
  createManualReservation,
  createManualAttendee,
  cancelBoothReservation,
  refundBoothReservation,
  expireStaleOrders,
  retryReservationZohoInvoice,
  resendReservationConfirmationEmail,
  Booth,
  AdminReservationItem,
  ManualReservationPayload,
  ManualAttendeePayload,
} from '../lib/api';
import {
  Building2,
  UserCheck,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Copy,
  ExternalLink,
  RefreshCw,
  Search,
  Sparkles,
  Ticket,
  FileText,
  BadgeCheck,
  Clock,
  Ban,
  RotateCcw,
  Mail,
} from 'lucide-react';

interface AdminOfflineBookingProps {
  adminKey: string;
  defaultForm?: 'stall' | 'attendee';
  activeSection?: 'booths' | 'visitors';
  hideSwitchTabs?: boolean;
}

export function AdminOfflineBooking({
  adminKey,
  defaultForm = 'stall',
  activeSection,
  hideSwitchTabs = false,
}: AdminOfflineBookingProps) {
  const queryClient = useQueryClient();

  // Active sub-tab
  const [activeForm, setActiveForm] = useState<'stall' | 'attendee'>(defaultForm);

  useEffect(() => {
    if (defaultForm) {
      setActiveForm(defaultForm);
    }
  }, [defaultForm]);

  // Stall Booking Form State
  const [selectedBoothId, setSelectedBoothId] = useState<string>('');
  const [stallCompany, setStallCompany] = useState('');
  const [stallContact, setStallContact] = useState('');
  const [stallEmail, setStallEmail] = useState('');
  const [stallPhone, setStallPhone] = useState('');
  const [stallGstin, setStallGstin] = useState('');
  const [stallAmount, setStallAmount] = useState<number | ''>('');
  const [stallPaymentMethod, setStallPaymentMethod] = useState<
    'neft_rtgs' | 'cheque' | 'comp_sponsor' | 'razorpay'
  >('neft_rtgs');
  const [stallBookingMode, setStallBookingMode] = useState<'booked' | 'hold_48h'>('booked');
  const [stallPaymentRef, setStallPaymentRef] = useState('');
  const [stallNotes, setStallNotes] = useState('');
  const [stallSuccess, setStallSuccess] = useState<{
    message: string;
    portalUrl: string;
    boothId: string;
    companyName: string;
  } | null>(null);

  // Attendee Pass Form State
  const [attendeeName, setAttendeeName] = useState('');
  const [attendeeEmail, setAttendeeEmail] = useState('');
  const [attendeePhone, setAttendeePhone] = useState('');
  const [attendeeCompany, setAttendeeCompany] = useState('');
  const [attendeeTier, setAttendeeTier] = useState<
    'trade_visitor' | 'conference_delegate' | 'vip' | 'exhibitor' | 'exhibitor_pass'
  >('conference_delegate');
  const [attendeePaymentMethod, setAttendeePaymentMethod] = useState<
    'offline' | 'neft_rtgs' | 'cheque' | 'exhibitor_comp' | 'razorpay'
  >('offline');
  const [attendeeRef, setAttendeeRef] = useState('');
  const [attendeeNotes, setAttendeeNotes] = useState('');
  const [attendeeSuccess, setAttendeeSuccess] = useState<{
    message: string;
    qrToken?: string;
    fullName: string;
  } | null>(null);

  const [copiedUrl, setCopiedUrl] = useState(false);
  const [reservationSearch, setReservationSearch] = useState('');

  // Fetch available booths for dropdown
  const {
    data: allBooths = [],
    isLoading: isBoothsLoading,
    refetch: refetchBooths,
  } = useQuery<Booth[]>({
    queryKey: ['booths'],
    queryFn: () => fetchBooths(),
    enabled: !!adminKey,
  });

  // Fetch existing reservations
  const {
    data: reservations = [],
    isLoading: isReservationsLoading,
    refetch: refetchReservations,
  } = useQuery<AdminReservationItem[]>({
    queryKey: ['admin-reservations', adminKey],
    queryFn: () => fetchAdminReservations(adminKey),
    enabled: !!adminKey,
  });

  const unbookedBooths = allBooths.filter((b) => b.status === 'available' || b.status === 'on_hold');

  // When booth is chosen, prefill default base amount
  const handleBoothSelect = (boothId: string) => {
    setSelectedBoothId(boothId);
    const found = allBooths.find((b) => b.id === boothId);
    if (found) {
      setStallAmount(Number(found.priceInr) || 0);
    }
  };

  // Mutation: Manual Stall Reservation
  const stallMutation = useMutation({
    mutationFn: (payload: ManualReservationPayload) => createManualReservation(payload, adminKey),
    onSuccess: (data) => {
      setStallSuccess({
        message: data.message,
        portalUrl: data.portalUrl,
        boothId: data.reservation.boothId,
        companyName: data.reservation.companyName,
      });
      // Reset form
      setSelectedBoothId('');
      setStallCompany('');
      setStallContact('');
      setStallEmail('');
      setStallPhone('');
      setStallGstin('');
      setStallAmount('');
      setStallPaymentRef('');
      setStallNotes('');
      setStallBookingMode('booked');
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['admin-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-system-health'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to complete offline stall booking');
    },
  });

  // Mutation: Manual Attendee Pass
  const attendeeMutation = useMutation({
    mutationFn: (payload: ManualAttendeePayload) => createManualAttendee(payload, adminKey),
    onSuccess: (data) => {
      setAttendeeSuccess({
        message: data.message,
        qrToken: data.attendee.qrToken,
        fullName: data.attendee.fullName,
      });
      // Reset form
      setAttendeeName('');
      setAttendeeEmail('');
      setAttendeePhone('');
      setAttendeeCompany('');
      setAttendeeRef('');
      setAttendeeNotes('');
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to issue manual delegate pass');
    },
  });

  // Mutation: Cancel Booth Reservation
  const cancelReservationMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      cancelBoothReservation(id, adminKey, reason),
    onSuccess: (data) => {
      alert(data.message);
      queryClient.invalidateQueries({ queryKey: ['admin-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to cancel reservation');
    },
  });

  // Mutation: Refund Booth Reservation
  const refundReservationMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      refundBoothReservation(id, adminKey, reason),
    onSuccess: (data) => {
      alert(data.message);
      queryClient.invalidateQueries({ queryKey: ['admin-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['booths'] });
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to refund reservation');
    },
  });

  // Mutation: Expire Stale Orders
  const expireOrdersMutation = useMutation({
    mutationFn: () => expireStaleOrders(adminKey),
    onSuccess: (data) => {
      alert(data.message);
      queryClient.invalidateQueries({ queryKey: ['admin-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to expire stale orders');
    },
  });

  // Mutation: Retry Zoho GST Invoice for Stall Reservation
  const retryReservationInvoiceMutation = useMutation({
    mutationFn: (reservationId: string) => retryReservationZohoInvoice(adminKey, reservationId),
    onSuccess: (data: any) => {
      alert(data?.message || 'Zoho GST Invoice generated successfully for stall reservation.');
      queryClient.invalidateQueries({ queryKey: ['admin-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-system-health'] });
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to generate Zoho GST invoice for stall reservation.');
    },
  });

  // Mutation: Resend Confirmation Email for Stall Reservation
  const resendReservationEmailMutation = useMutation({
    mutationFn: ({ id, email }: { id: string; email?: string }) =>
      resendReservationConfirmationEmail(adminKey, id, email ? { email } : {}),
    onSuccess: (data: any) => {
      alert(data?.message || 'Stall booking confirmation email dispatched successfully.');
      queryClient.invalidateQueries({ queryKey: ['admin-reservations'] });
    },
    onError: (err: any) => {
      alert(err?.message || 'Failed to resend stall booking confirmation email.');
    },
  });

  const handleStallSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBoothId) {
      alert('Please select an unbooked exhibition stall');
      return;
    }
    stallMutation.mutate({
      boothId: selectedBoothId,
      companyName: stallCompany.trim(),
      contactPerson: stallContact.trim(),
      email: stallEmail.trim(),
      phone: stallPhone.trim(),
      gstin: stallGstin.trim() || undefined,
      agreedAmountInr: stallAmount === '' ? undefined : Number(stallAmount),
      paymentMethod: stallPaymentMethod,
      bookingMode: stallBookingMode,
      paymentReference: stallPaymentRef.trim() || undefined,
      notes: stallNotes.trim() || undefined,
    });
  };

  const handleAttendeeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    attendeeMutation.mutate({
      fullName: attendeeName.trim(),
      email: attendeeEmail.trim(),
      phone: attendeePhone.trim(),
      companyName: attendeeCompany.trim() || undefined,
      tier: attendeeTier,
      paymentMethod: attendeePaymentMethod,
      paymentReference: attendeeRef.trim() || undefined,
      notes: attendeeNotes.trim() || undefined,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 3000);
  };

  const filteredReservations = reservations.filter((r) => {
    const q = reservationSearch.toLowerCase();
    return (
      r.boothId.toLowerCase().includes(q) ||
      r.companyName.toLowerCase().includes(q) ||
      r.contactEmail.toLowerCase().includes(q) ||
      (r.paymentReference && r.paymentReference.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Subnavigation Bar */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4 flex-wrap gap-4">
        {!hideSwitchTabs ? (
          <div className="flex items-center gap-2 bg-white p-1 rounded-xl border border-slate-200 shadow-xs">
            <button
              type="button"
              onClick={() => setActiveForm('stall')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeForm === 'stall'
                  ? 'bg-[#0A3D27] text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Building2 className="w-4 h-4" />
              <span>Manual Stall Booking (Offline)</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveForm('attendee')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeForm === 'attendee'
                  ? 'bg-[#0A3D27] text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <UserCheck className="w-4 h-4" />
              <span>Issue Paid / Comp Delegate Pass</span>
            </button>
          </div>
        ) : (
          <div className="text-xs font-bold text-[#0A3D27] flex items-center gap-2">
            {activeForm === 'stall' ? (
              <>
                <Building2 className="w-4 h-4" />
                <span>Offline Stall Allocation & Commercial Invoicing</span>
              </>
            ) : (
              <>
                <UserCheck className="w-4 h-4" />
                <span>VIP & Offline Delegate Pass Issuance</span>
              </>
            )}
          </div>
        )}

        <button
          onClick={() => {
            refetchBooths();
            refetchReservations();
          }}
          disabled={isBoothsLoading || isReservationsLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors cursor-pointer border border-slate-200"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${isBoothsLoading || isReservationsLoading ? 'animate-spin text-[#0A3D27]' : ''}`}
          />
          <span>Refresh Stalls</span>
        </button>
      </div>

      {/* FORM 1: MANUAL STALL BOOKING */}
      {activeForm === 'stall' && (
        <div className="bg-white border border-slate-200/90 rounded-2xl p-6 lg:p-8 relative overflow-hidden shadow-xs">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900">Manual Stall Booking & Allocation</h3>
              <p className="text-xs text-slate-500">
                Directly book unreserved or held exhibition stalls for offline NEFT/RTGS, Cheque, or Sponsor allocations.
              </p>
            </div>
          </div>

          {/* Success Banner */}
          {stallSuccess && (
            <div className="mb-8 p-5 bg-emerald-50 border border-emerald-200 rounded-xl space-y-3 animate-in fade-in">
              <div className="flex items-center gap-2 text-[#0A3D27] font-bold text-sm">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <span>{stallSuccess.message}</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Stall <strong>{stallSuccess.boothId}</strong> has been locked and confirmed for{' '}
                <strong>{stallSuccess.companyName}</strong>. A confirmation email has been dispatched with their portal link.
              </p>
              <div className="bg-[#F8F9F6] p-3 rounded-lg border border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                <div className="truncate text-xs text-[#0A3D27] font-mono font-bold">
                  {stallSuccess.portalUrl}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copyToClipboard(stallSuccess.portalUrl)}
                    className="flex items-center gap-1.5 px-3 py-1 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer shadow-xs"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>{copiedUrl ? 'Copied!' : 'Copy Portal Link'}</span>
                  </button>
                  <a
                    href={stallSuccess.portalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1 bg-[#0A3D27] hover:bg-[#062B1B] text-xs font-bold text-white rounded-lg transition-colors cursor-pointer shadow-xs"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Open Portal</span>
                  </a>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleStallSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Stall Selection */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                  <span>Select Unbooked Stall *</span>
                  <span className="text-[11px] font-normal text-[#0A3D27]">
                    {unbookedBooths.length} stalls available
                  </span>
                </label>
                <select
                  value={selectedBoothId}
                  onChange={(e) => handleBoothSelect(e.target.value)}
                  required
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] font-medium cursor-pointer"
                >
                  <option value="">-- Choose an available stall --</option>
                  {unbookedBooths.map((b) => (
                    <option key={b.id} value={b.id}>
                      Stall {b.id} ({b.dimensions}) — ₹{Number(b.priceInr).toLocaleString('en-IN')}{' '}
                      {b.status === 'on_hold' ? '[ON HOLD]' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Company Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Exhibiting Company Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. AgroTech Innovations Ltd."
                  value={stallCompany}
                  onChange={(e) => setStallCompany(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Contact Person */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Primary Contact Person *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Ramesh Patel"
                  value={stallContact}
                  onChange={(e) => setStallContact(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Contact Email *</label>
                <input
                  type="email"
                  required
                  placeholder="contact@company.com"
                  value={stallEmail}
                  onChange={(e) => setStallEmail(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Contact Phone *</label>
                <input
                  type="tel"
                  required
                  placeholder="+91 98765 43210"
                  value={stallPhone}
                  onChange={(e) => setStallPhone(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* GSTIN */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">GSTIN (Optional)</label>
                <input
                  type="text"
                  placeholder="27AAAAA0000A1Z5"
                  value={stallGstin}
                  onChange={(e) => setStallGstin(e.target.value.toUpperCase())}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] uppercase"
                />
              </div>

              {/* Agreed Base Amount */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Agreed Amount (INR)</label>
                <input
                  type="number"
                  placeholder="e.g. 150000"
                  value={stallAmount}
                  onChange={(e) => setStallAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Payment Mode */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Payment Mode *</label>
                <select
                  value={stallPaymentMethod}
                  onChange={(e: any) => setStallPaymentMethod(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] cursor-pointer"
                >
                  <option value="neft_rtgs">NEFT / RTGS Bank Wire</option>
                  <option value="cheque">Cheque / Demand Draft</option>
                  <option value="comp_sponsor">Complimentary / Sponsor Allocation</option>
                  <option value="razorpay">Direct Online (Razorpay)</option>
                </select>
              </div>

              {/* Allocation Mode: Immediate Confirmation vs 48-Hour NEFT/RTGS Hold */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Allocation Status *</label>
                <select
                  value={stallBookingMode}
                  onChange={(e) => setStallBookingMode(e.target.value as 'booked' | 'hold_48h')}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] cursor-pointer font-semibold"
                >
                  <option value="booked">✅ Confirm &amp; Issue GST Invoice Immediately</option>
                  <option value="hold_48h">⏳ Place 48-Hour Offline NEFT/RTGS Hold</option>
                </select>
              </div>

              {/* Payment Reference / UTR */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">
                  {stallBookingMode === 'hold_48h' ? 'Proforma / PO Reference (Optional)' : 'Bank UTR / Cheque No.'}
                </label>
                <input
                  type="text"
                  placeholder={
                    stallBookingMode === 'hold_48h'
                      ? 'e.g. Awaiting NEFT Wire / PO-2027-09'
                      : 'e.g. UTR1234567890 / CHQ-4029'
                  }
                  value={stallPaymentRef}
                  onChange={(e) => setStallPaymentRef(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>
            </div>

            {stallBookingMode === 'hold_48h' && (
              <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2.5">
                <Clock className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <strong>48-Hour Offline NEFT/RTGS Hold:</strong> This stall will be placed on an exclusive 48-hour hold (`ON HOLD`) while awaiting bank wire settlement. You will receive automated founder alerts at 36 hours (warning) and 48 hours (expiry reminder) so the stall never auto-releases while a bank wire is in transit.
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Internal Remarks / Notes (Optional)</label>
              <textarea
                rows={2}
                placeholder="Special corner position agreed with organizer secretariat..."
                value={stallNotes}
                onChange={(e) => setStallNotes(e.target.value)}
                className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={stallMutation.isPending || !selectedBoothId}
                className="flex items-center gap-2 px-6 py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs disabled:opacity-50 cursor-pointer"
              >
                {stallMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{stallBookingMode === 'hold_48h' ? 'Placing 48h Hold...' : 'Confirming Allocation...'}</span>
                  </>
                ) : (
                  <>
                    <BadgeCheck className="w-4 h-4" />
                    <span>
                      {stallBookingMode === 'hold_48h'
                        ? 'Place 48-Hour NEFT Hold'
                        : 'Confirm & Lock Stall'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FORM 2: MANUAL ATTENDEE PASS */}
      {activeForm === 'attendee' && (
        <div className="bg-white border border-slate-200/90 rounded-2xl p-6 lg:p-8 relative overflow-hidden shadow-xs">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] flex items-center justify-center">
              <Ticket className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900">Manual Delegate Pass Issue</h3>
              <p className="text-xs text-slate-500">
                Directly issue Trade Visitor, Conference Delegate, or VIP badges for VIP guests, offline delegates, or staff.
              </p>
            </div>
          </div>

          {/* Success Banner */}
          {attendeeSuccess && (
            <div className="mb-8 p-5 bg-[#FAF5EA] border border-[#E6C687] rounded-xl space-y-2 animate-in fade-in">
              <div className="flex items-center gap-2 text-[#785613] font-bold text-sm">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <span>{attendeeSuccess.message}</span>
              </div>
              <p className="text-xs text-slate-700">
                Digital pass has been generated with QR token{' '}
                <strong className="text-[#0A3D27] font-mono">{attendeeSuccess.qrToken}</strong> and emailed to{' '}
                <strong>{attendeeSuccess.fullName}</strong>.
              </p>
            </div>
          )}

          <form onSubmit={handleAttendeeSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Select Tier */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Pass Tier *</label>
                <select
                  value={attendeeTier}
                  onChange={(e: any) => setAttendeeTier(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] cursor-pointer"
                >
                  <option value="trade_visitor">Trade Visitor (Expo Hall Entry)</option>
                  <option value="conference_delegate">Conference Delegate (2-Day Full Access)</option>
                  <option value="vip">VIP Buyer & Dignitary</option>
                  <option value="exhibitor">Official Exhibitor Staff</option>
                </select>
              </div>

              {/* Delegate Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Dr. Anand Sharma"
                  value={attendeeName}
                  onChange={(e) => setAttendeeName(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Email Address *</label>
                <input
                  type="email"
                  required
                  placeholder="delegate@domain.com"
                  value={attendeeEmail}
                  onChange={(e) => setAttendeeEmail(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Phone Number *</label>
                <input
                  type="tel"
                  required
                  placeholder="+91 98000 12345"
                  value={attendeePhone}
                  onChange={(e) => setAttendeePhone(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Company */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Company / Organization</label>
                <input
                  type="text"
                  placeholder="e.g. National Seeds Corp"
                  value={attendeeCompany}
                  onChange={(e) => setAttendeeCompany(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              {/* Payment Mode */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Payment Allocation *</label>
                <select
                  value={attendeePaymentMethod}
                  onChange={(e: any) => setAttendeePaymentMethod(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] cursor-pointer"
                >
                  <option value="offline">Offline / Comp Guest</option>
                  <option value="neft_rtgs">NEFT / Wire Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="exhibitor_comp">Exhibitor Comp</option>
                  <option value="razorpay">Online (Razorpay)</option>
                </select>
              </div>

              {/* Reference Note */}
              <div className="space-y-1.5 md:col-span-2 lg:col-span-3">
                <label className="text-xs font-bold text-slate-700">Reference / Authorization Note</label>
                <input
                  type="text"
                  placeholder="e.g. Approved by Secretariat Chair / Group Booking PO-401"
                  value={attendeeRef}
                  onChange={(e) => setAttendeeRef(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={attendeeMutation.isPending}
                className="flex items-center gap-2 px-6 py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs disabled:opacity-50 cursor-pointer"
              >
                {attendeeMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Issuing Digital Pass...</span>
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4" />
                    <span>Issue Paid Pass</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* RESERVATIONS & PORTAL AUDIT TABLE — ONLY ON BOOTHS / STALL VIEW */}
      {activeSection !== 'visitors' && activeForm !== 'attendee' && (
        <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-4 bg-slate-50/50">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Stall Bookings &amp; Exhibitor Portal Registry</h3>
              <p className="text-xs text-slate-500">
                Review confirmed bookings, Zoho GST invoices, and 1-click recovery actions
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Expire all pending attendee and stall orders older than 24 hours?')) {
                    expireOrdersMutation.mutate();
                  }
                }}
                disabled={expireOrdersMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl border border-slate-300 transition-colors cursor-pointer shadow-xs disabled:opacity-50"
              >
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <span>{expireOrdersMutation.isPending ? 'Expiring...' : 'Expire Stale Orders (>24h)'}</span>
              </button>

              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Search by Stall, Company, Email..."
                  value={reservationSearch}
                  onChange={(e) => setReservationSearch(e.target.value)}
                  className="bg-[#F8F9F6] border border-slate-300 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4">Stall ID</th>
                  <th className="py-3 px-4">Company</th>
                  <th className="py-3 px-4">Contact</th>
                  <th className="py-3 px-4">Agreed INR</th>
                  <th className="py-3 px-4">Status &amp; GST Invoice</th>
                  <th className="py-3 px-4">Payment Mode</th>
                  <th className="py-3 px-4">Reference / UTR</th>
                  <th className="py-3 px-4">Comp Passes</th>
                  <th className="py-3 px-4 text-right">Actions &amp; Recovery</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReservations.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-slate-500 text-xs">
                      {isReservationsLoading ? 'Loading reservations...' : 'No reservations found.'}
                    </td>
                  </tr>
                ) : (
                  filteredReservations.map((r) => {
                    const portalUrl = `${window.location.origin}/exhibitor-portal?token=${r.portalToken}`;
                    const isOfflineHold = r.status === 'pending_offline' || r.isOfflineHold;
                    const isBooked = !r.status || r.status === 'booked';
                    const invoiceNum = (r as any).zohoInvoiceNumber || (r as any).zohoInvoiceId;
                    const invoiceUrl = (r as any).zohoInvoiceUrl;
                    return (
                      <tr key={r.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-mono font-bold text-[#0A3D27]">
                          {r.boothId}
                        </td>
                        <td className="py-3 px-4 font-semibold text-slate-900">
                          {r.companyName}
                          {r.fasciaName && (
                            <span className="block text-[10px] text-slate-500 font-normal">
                              Fascia: {r.fasciaName}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div>{r.contactEmail}</div>
                          {r.contactPhone && (
                            <div className="text-[10px] text-slate-500">{r.contactPhone}</div>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono font-medium text-slate-900">
                          ₹{Number(r.agreedAmountInr || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-4 space-y-1">
                          <div>
                            {r.status === 'cancelled' ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-300">
                                Cancelled
                              </span>
                            ) : r.status === 'refunded' ? (
                              <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-300">
                                Refunded
                              </span>
                            ) : isOfflineHold ? (
                              r.holdAlertStatus === 'expired_48h' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-300">
                                  <AlertTriangle className="w-3 h-3" />
                                  <span>48h Hold Expired ({r.holdHoursElapsed}h)</span>
                                </span>
                              ) : r.holdAlertStatus === 'warning_36h' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                  <Clock className="w-3 h-3" />
                                  <span>36h Warning ({r.holdHoursRemaining}h left)</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-300">
                                  <Clock className="w-3 h-3" />
                                  <span>48h NEFT Hold ({r.holdHoursRemaining ?? 48}h left)</span>
                                </span>
                              )
                            ) : (
                              <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-[#0A3D27] border border-emerald-300">
                                Confirmed
                              </span>
                            )}
                          </div>
                          {isBooked && (
                            <div>
                              {invoiceNum ? (
                                invoiceUrl ? (
                                  <a
                                    href={invoiceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-[#0A3D27] hover:underline"
                                  >
                                    <FileText className="w-3 h-3" />
                                    <span>{invoiceNum}</span>
                                  </a>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-slate-700">
                                    <FileText className="w-3 h-3 text-[#0A3D27]" />
                                    <span>{invoiceNum}</span>
                                  </span>
                                )
                              ) : r.paymentMethod !== 'comp_sponsor' ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-bold">
                                  <AlertTriangle className="w-2.5 h-2.5" />
                                  <span>Missing Invoice</span>
                                </span>
                              ) : null}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200 uppercase">
                            {r.paymentMethod.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-[11px] text-slate-500">
                          {r.paymentReference || '—'}
                        </td>
                        <td className="py-3 px-4">
                          {r.compPassesClaimed ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#0A3D27] font-semibold">
                              <CheckCircle2 className="w-3 h-3" />
                              <span>Claimed</span>
                            </span>
                          ) : (
                            <span className="text-[11px] text-amber-700 font-semibold">
                              Pending (2)
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {isOfflineHold && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const utr = window.prompt(
                                      `Enter NEFT/RTGS UTR or Cheque reference to confirm Stall ${r.boothId} for ${r.companyName}:`,
                                      r.paymentReference || ''
                                    );
                                    if (utr !== null) {
                                      stallMutation.mutate({
                                        boothId: r.boothId,
                                        companyName: r.companyName,
                                        contactPerson: r.companyName,
                                        email: r.contactEmail,
                                        phone: r.contactPhone || '+91 9800000000',
                                        gstin: r.gstin || undefined,
                                        agreedAmountInr: Number(r.agreedAmountInr || 0),
                                        paymentMethod: (r.paymentMethod as any) || 'neft_rtgs',
                                        bookingMode: 'booked',
                                        paymentReference: utr.trim() || undefined,
                                      });
                                    }
                                  }}
                                  disabled={stallMutation.isPending}
                                  title="Confirm Offline Wire & Generate Zoho GST Invoice"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  <BadgeCheck className="w-3 h-3" />
                                  <span>Confirm Wire</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Release 48-hour hold on Stall ${r.boothId} (${r.companyName}) and return stall to public inventory?`
                                      )
                                    ) {
                                      cancelReservationMutation.mutate({
                                        id: r.id,
                                        reason: '48-hour offline NEFT hold released by admin',
                                      });
                                    }
                                  }}
                                  disabled={cancelReservationMutation.isPending}
                                  title="Release 48h Hold & Free Stall"
                                  className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  Release Hold
                                </button>
                              </>
                            )}
                            {isBooked && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => retryReservationInvoiceMutation.mutate(r.id)}
                                  disabled={retryReservationInvoiceMutation.isPending}
                                  title="Retry / Regenerate Zoho GST Invoice"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-[#0A3D27] border border-emerald-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  <FileText className="w-3 h-3" />
                                  <span>Retry Zoho Invoice</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updatedEmail = window.prompt(
                                      'Confirm or update recipient email address to resend stall confirmation:',
                                      r.contactEmail
                                    );
                                    if (updatedEmail !== null && updatedEmail.trim()) {
                                      resendReservationEmailMutation.mutate({
                                        id: r.id,
                                        email: updatedEmail.trim(),
                                      });
                                    }
                                  }}
                                  disabled={resendReservationEmailMutation.isPending}
                                  title="Resend Confirmation Email / Fix Email"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  <Mail className="w-3 h-3" />
                                  <span>Resend Email</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to cancel the reservation for stall ${r.boothId}? The stall will be freed for booking.`)) {
                                      cancelReservationMutation.mutate({ id: r.id });
                                    }
                                  }}
                                  disabled={cancelReservationMutation.isPending}
                                  title="Cancel Reservation & Free Stall"
                                  className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (window.confirm(`Mark reservation for stall ${r.boothId} as refunded?`)) {
                                      refundReservationMutation.mutate({ id: r.id });
                                    }
                                  }}
                                  disabled={refundReservationMutation.isPending}
                                  title="Refund Reservation & Free Stall"
                                  className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  Refund
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => copyToClipboard(portalUrl)}
                              title="Copy Portal URL"
                              className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer shadow-xs"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            <a
                              href={portalUrl}
                              target="_blank"
                              rel="noreferrer"
                              title="Open Exhibitor Portal"
                              className="p-1.5 bg-[#FAF5EA] hover:bg-[#F3E8CE] text-[#785613] border border-[#E6C687] rounded-lg transition-colors cursor-pointer shadow-xs"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
